import { useEffect, useState } from "react";

import { apis, callApi } from "../api";
import { useAccount } from "./useAccount";
import { TPaymentRequest } from "../@types";

export type TPaymentRequestService = {
	loading: boolean;
	paymentRequests: TPaymentRequest[];
	listeners: (() => void)[];

	addEventListener: (cb: () => void) => void;
	emitChanges: () => void;
	removeEventListener: (cb: () => void) => void;
	clearData: () => void;

	setLoading: (loading: boolean) => void;
	setPaymentRequests: (paymentRequests: TPaymentRequest[]) => void;
	cancelPaymentRequests: (id: number, cancelReason: string) => void;
};

export const PaymentRequestService: TPaymentRequestService = {
	loading: false,
	paymentRequests: [],
	listeners: [],

	addEventListener(cb) {
		this.listeners = this.listeners.filter((listener) => listener !== cb);
		this.listeners.push(cb);
	},

	emitChanges() {
		for (const listener of this.listeners) {
			listener();
		}
	},

	removeEventListener(cb) {
		this.listeners = this.listeners.filter((listener) => listener !== cb);
	},

	clearData() {
		this.paymentRequests = [];
		this.emitChanges();
	},

	setLoading(loading) {
		this.loading = loading;
	},

	setPaymentRequests(paymentRequests) {
		this.paymentRequests = paymentRequests;
	},

	cancelPaymentRequests(id: number, cancelReason: string) {
		const index = this.paymentRequests.findIndex(
			(paymentRequest) => paymentRequest.id === id,
		);

		if (index === -1) {
			return;
		}

		this.paymentRequests[index] = {
			...this.paymentRequests[index],
			status: "Cancelled",
			cancelReason,
		};

		this.paymentRequests = [...this.paymentRequests];
	},
};

export function usePaymentRequestInit() {
	const { account } = useAccount();

	useEffect(() => {
		const getBills = async () => {
			try {
				PaymentRequestService.setLoading(true);
				PaymentRequestService.emitChanges();

				const response = await callApi({
					method: "GET",
					url: apis.BILL,
				});

				PaymentRequestService.setPaymentRequests(response.bills);
			} catch (error) {
			} finally {
				PaymentRequestService.setLoading(false);
				PaymentRequestService.emitChanges();
			}
		};

		if (account?.id) {
			getBills();
		}
	}, [account?.id]);
}

export function usePaymentRequest() {
	const [loading, setLoading] = useState(PaymentRequestService.loading);
	const [paymentRequests, setPaymentRequests] = useState(
		PaymentRequestService.paymentRequests,
	);
	const { account } = useAccount();

	useEffect(() => {
		const updateData = () => {
			setLoading(PaymentRequestService.loading);
			setPaymentRequests(PaymentRequestService.paymentRequests);
		};

		PaymentRequestService.addEventListener(updateData);

		return () => {
			PaymentRequestService.removeEventListener(updateData);
		};
	}, []);

	const cancelPaymentRequest = async (
		id: number,
		body: { cancelReason: string },
	) => {
		try {
			const response = await callApi({
				method: "PATCH",
				url: `${apis.BILL}/${id}/cancel`,
				body,
			});

			if (!response) {
				return;
			}

			PaymentRequestService.cancelPaymentRequests(id, body.cancelReason);
			PaymentRequestService.emitChanges();
		} catch (error) {}
	};

	const unpaidPaymentRequests = paymentRequests.filter(
		(paymentRequest) =>
			paymentRequest.userId === account?.id &&
			paymentRequest.status === "Issued",
	);

	const countUnpaidPaymentRequest = unpaidPaymentRequests.length;

	return {
		loading,
		paymentRequests,
		unpaidPaymentRequests,
		countUnpaidPaymentRequest,
		cancelPaymentRequest,
	};
}
